import { useGetActivityAgendaList } from "apps/console/modules/program/workflow/associateProgram/agenda/list/dataloader";
import { useAssociatedProgramBehaviourFactory } from "apps/console/modules/program/workflow/associateProgram/profile/activity/details/components/associatedProgramBehaviourFactory";
import { useParticipantDetails } from "apps/myExperience/context/participantDetails";
import React from "react";
import { useParams } from "react-router-dom";
import { useRegDetail } from "shared/context/regDetail";
import AssociatedProgramStatus, { AssociatedProgramStatusCheck } from "shared/enums/AssociateProgramStatus";
import UtilityDataModule from "shared/enums/UtilityDataModule";
import { FormFieldOption } from "shared/form/fields/types";
import DateTimeHelper from "shared/utils/dateTimeHelper";
import { DateRepresentationEnum } from "shared/utils/dateTimeHelper/enums";
import useLogAnalyticsScreenHook from "apps/myExperience/context/analyticsWrapper/hooks/useLogAnalyticsScreenHook";
import { AnalyticsScreenNameEnum } from "shared/enums/AnalyticsEnums";

export function useActivityAgendaListingForMarkAttendanceInMyExperienceService() {
  const { programParticipantId = "", agendaId = "" } = useParams();
  const behavior = useAssociatedProgramBehaviourFactory();
  useLogAnalyticsScreenHook({
    properties: { screenType: AnalyticsScreenNameEnum.MANAGE_ACTIVITY_LISTING, programParticipantId }
  });
  const { data: agendas } = useGetActivityAgendaList(
    behavior.getApIs().agendaListForMarkAttendance(programParticipantId)
  );
  const [filters, setFilters] = React.useState<Array<FormFieldOption>>([]);
  const { getProgramDetails } = useParticipantDetails();
  const { id: registrationId } = useRegDetail();
  const dateOptions = React.useMemo(() => {
    const uniqueDates = new Set();
    agendas?.forEach(agenda => {
      uniqueDates.add(DateTimeHelper.getExactDate(agenda.time.timeFrom, DateRepresentationEnum.DATE));
    });
    return Array.from(uniqueDates)
      .sort()
      .map((date: any) => ({
        label: DateTimeHelper.formatDate({ startDate: date, option: { showWeekDay: true, showYear: false } }),
        value: date
      }));
  }, [agendas]);

  const filteredData = React.useMemo(() => {
    if (filters.length === 0) {
      return agendas;
    }
    const timeFromValues = filters.map(filter => filter.value);
    return agendas?.filter(agenda =>
      timeFromValues.includes(DateTimeHelper.getExactDate(agenda.time.timeFrom, DateRepresentationEnum.DATE))
    );
  }, [filters, agendas]);

  const selectedAgenda = React.useMemo(() => {
    return agendas?.find(agenda => agenda.id === agendaId);
  }, [agendaId, agendas]);

  const programId = React.useMemo(() => {
    const loggedInUserProgramParticipantId = programParticipantId;
    return loggedInUserProgramParticipantId && getProgramDetails(loggedInUserProgramParticipantId)?.id;
  }, [programParticipantId, getProgramDetails]);

  const programStatus = React.useMemo(() => {
    return getProgramDetails(programParticipantId)?.status as AssociatedProgramStatus;
  }, [programParticipantId, getProgramDetails]);

  const canShowMarkAttendanceButton = React.useMemo(() => {
    return !!programStatus && behavior.canShowMarkAttendanceButton({ programStatus });
  }, [programStatus, behavior]);

  const canShowEllapsedTag = React.useMemo(() => {
    return !!programStatus && behavior.canShowMarkAttendanceButton({ programStatus });
  }, [behavior, programStatus]);

  const canShowAttendanceBlock = React.useMemo(() => {
    return !!programStatus && AssociatedProgramStatusCheck.isAfterInvitesSent(programStatus);
  }, [programStatus]);

  const utilityDataModule: UtilityDataModule | null = React.useMemo(() => {
    switch (programStatus) {
      case AssociatedProgramStatus.INVITES_SENT:
        return UtilityDataModule.ACTIVITY_ADMIN_MANAGE_ACTIVITIES;
      case AssociatedProgramStatus.IN_PROGRESS:
        return UtilityDataModule.ACTIVITY_ADMIN_MARK_ATTENDANCE;
      default:
        return null;
    }
  }, [programStatus]);

  return {
    dateOptions,
    filters,
    setFilters,
    agendas: filteredData,
    registrationId,
    programId,
    selectedAgenda,
    canShowMarkAttendanceButton,
    canShowEllapsedTag,
    canShowAttendanceBlock,
    utilityDataModule
  };
}
